import { Link } from 'react-router-dom'
import { ArrowRight, Leaf, Heart, Users, MapPin, Quote } from 'lucide-react'
import MarketingHeader from '../components/MarketingHeader'
import MarketingFooter from '../components/MarketingFooter'

const values = [
  {
    icon: Users,
    title: 'Community first',
    body: 'Every feature we build asks one question: does this make neighbors more connected, or less? Profit follows trust, not the other way around.',
  },
  {
    icon: Leaf,
    title: 'Reduce, reuse, rent',
    body: 'Manufacturing a new kayak emits roughly 400 lbs of CO₂. Renting one that already exists emits zero. We believe access beats ownership.',
  },
  {
    icon: Heart,
    title: 'Radically honest',
    body: 'No hidden fees. No fine print designed to confuse. Every policy written in plain English and posted publicly before we enforce it.',
  },
  {
    icon: MapPin,
    title: 'Deeply local',
    body: 'We are building in Alaska first because we live here and understand the needs here. We will only expand where we can truly serve.',
  },
]

const milestones = [
  { year: '2024', label: 'Idea born', detail: 'Jordan rents a kayak from a stranger via Facebook Marketplace. The experience is great. The process is chaotic.' },
  { year: 'Q1 2025', label: 'Team forms', detail: 'Maya, Darius, and Priya join. First whiteboard sessions in a Midtown Anchorage coffee shop.' },
  { year: 'Q3 2025', label: 'Seed round', detail: '$1.2M raised from Alaska-based angels and one national climate-tech fund.' },
  { year: 'Q1 2026', label: 'Beta launch', detail: '120 beta users, 340 listings, and a 98% satisfaction rate in our first three months.' },
  { year: 'Summer 2026', label: 'Public launch', detail: 'Opening to all Alaskans. Waitlist now open — join thousands who are ready.' },
]

const testimonials = [
  {
    quote: 'I made $1,400 last summer renting out gear I barely use. It paid for my own camping trip twice over.',
    name: 'Sarah K.',
    location: 'Eagle River, AK',
    image: '/images/marketing/testimonial-sarah.jpg',
  },
  {
    quote: 'As a visitor to Alaska I got a kayak, a tent, and a dry bag all from one neighborhood. Saved me $600 vs. renting from a commercial outfitter.',
    name: 'Marcus T.',
    location: 'Visiting from Portland, OR',
    image: '/images/marketing/testimonial-marcus.jpg',
  },
  {
    quote: 'The inspection checklist and escrow payment gave me confidence as a first-time renter. I knew exactly what I was getting.',
    name: 'Lisa M.',
    location: 'Homer, AK',
    image: '/images/marketing/testimonial-lisa.jpg',
  },
]

export default function About() {
  return (
    <div className="min-h-screen bg-background text-foreground" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <MarketingHeader alwaysSolid />

      {/* ── Hero ── */}
      <section className="relative pt-32 pb-0 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-end pb-0">
          <div className="pb-16">
            <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-4">Our Story</p>
            <h1
              className="text-5xl md:text-6xl font-semibold leading-tight mb-6"
              style={{ fontFamily: "'Fraunces', serif" }}
            >
              We believe in
              <br />
              <span className="italic font-light text-primary">access over ownership</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed max-w-lg">
              I Got That U.S. started with one question: why do neighbors buy the same gear five times
              over when they could share it once? We are building the infrastructure to make sharing
              the obvious choice in Alaska — and eventually everywhere.
            </p>
          </div>
          <div className="relative h-80 md:h-[520px] rounded-t-2xl overflow-hidden bg-muted">
            <img
              src="/images/marketing/about-hero-wilderness.jpg"
              alt="Alaska wilderness lake and mountains"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/30 to-transparent" />
          </div>
        </div>
      </section>

      {/* ── Mission strip ── */}
      <section className="bg-primary py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-primary-foreground/60 text-sm uppercase tracking-widest font-semibold mb-3">Our Mission</p>
          <blockquote
            className="text-2xl md:text-3xl font-semibold !text-white leading-snug"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            "To make renting from neighbors so easy, safe, and reliable that buying things you rarely use becomes the less obvious choice."
          </blockquote>
        </div>
      </section>

      {/* ── Values ── */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-3">What guides us</p>
            <h2 className="text-4xl md:text-5xl font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>Our values</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => {
              const Icon = v.icon
              return (
                <div key={v.title} className="bg-card rounded-xl p-7 border border-border hover:shadow-md transition-shadow">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                    <Icon size={20} className="text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-3" style={{ fontFamily: "'Fraunces', serif" }}>{v.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{v.body}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── Story / Timeline ── */}
      <section className="py-20 px-6 bg-secondary">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-3">Timeline</p>
            <h2 className="text-4xl font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>How we got here</h2>
          </div>
          <div className="relative border-l-2 border-primary/20 pl-10 flex flex-col gap-10 ml-4">
            {milestones.map((m) => (
              <div key={m.year} className="relative">
                <div className="absolute -left-[calc(2.5rem+1px)] top-0 w-5 h-5 rounded-full bg-primary border-4 border-background" />
                <span className="text-xs font-semibold text-primary uppercase tracking-widest">{m.year}</span>
                <h4 className="font-semibold text-lg mt-0.5 mb-1" style={{ fontFamily: "'Fraunces', serif" }}>{m.label}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{m.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="py-20 px-6 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold tracking-widest text-accent uppercase mb-3">From the community</p>
            <h2 className="text-4xl font-semibold !text-white" style={{ fontFamily: "'Fraunces', serif" }}>
              What neighbors are saying
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white/8 rounded-xl p-7 border border-white/10">
                <Quote size={24} className="text-accent mb-4 opacity-60" />
                <p className="text-primary-foreground/90 leading-relaxed mb-6 italic">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="text-sm font-semibold text-white">{t.name}</p>
                    <p className="text-xs text-primary-foreground/50">{t.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Impact stats ── */}
      <section className="py-16 px-6 bg-accent">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-accent-foreground" style={{ fontFamily: "'Fraunces', serif" }}>
              Our impact so far
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: '340+', label: 'Verified hosts' },
              { value: '2,400+', label: 'Successful rentals' },
              { value: '$142K', label: 'Earned by hosts' },
              { value: '18 tons', label: 'CO₂ avoided (est.)' },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-4xl font-semibold text-accent-foreground" style={{ fontFamily: "'Fraunces', serif" }}>{s.value}</div>
                <div className="text-sm text-accent-foreground/70 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 px-6 bg-secondary">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-semibold mb-4" style={{ fontFamily: "'Fraunces', serif" }}>
            Join us this summer
          </h2>
          <p className="text-muted-foreground mb-8 leading-relaxed">
            I Got That U.S. launches in Alaska in Summer 2026. Be among the first renters and owners — early
            members get priority listing placement and waived service fees for 90 days.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/browse"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors"
            >
              Browse early listings <ArrowRight size={15} />
            </Link>
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-card text-foreground font-semibold text-sm hover:bg-card/80 transition-colors border border-border"
            >
              How it works
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
